import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, ShapesIcon, TimerIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/08c57fac-ef4a-4d7c-b99c-1dd88765c6de/1792558658119_gk-mockup-ed78cbda.webp';
export const heroGuarantees = ['Direkte nedlasting', 'Engangsbetaling', 'Ingen abonnement', '7 dager pengene-tilbake-garanti'];
export const stats = [{
  value: '6',
  label: 'Digitale volumer'
}, {
  value: '240',
  label: 'Sider klare til bruk'
}, {
  value: '5',
  label: 'Bonuser inkludert'
}, {
  value: 'PDF',
  label: 'Klar til utskrift'
}, {
  value: '1–10',
  label: '1.–10. trinn i ett system'
}];
export const differenceStats = [{
  value: '1.–10. trinn',
  label: 'Fra lek og motorikk til egen treningsplan'
}, {
  value: '6 digitale volumer',
  label: 'Fra årsplan og timer til vurdering og underveisvurdering'
}, {
  value: 'PDF klar til utskrift',
  label: 'Bruk digitalt eller ta med i gymsalen'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Søndagskvelden går til planlegging',
  text: 'Du sitter foran PC-en søndag kveld og planlegger ukas gymtimer – og blir likevel ikke ferdig.'
}, {
  title: 'Elever som står i kø',
  text: 'Når elevene venter på tur, faller energien og timen mister tempo.'
}, {
  title: 'Blandede nivåer i samme gruppe',
  text: 'Noen er sterke og vante, andre er usikre – og timen skal fungere for alle.'
}, {
  title: 'Rektor spør etter vurdering',
  text: 'Kjennetegn, underveisvurdering og dokumentasjon blir enda mer arbeid etter timene.'
}, {
  title: 'Gymsalen kl. 08.15 – og ingenting er klart',
  text: 'Når tiden ikke strekker til, blir det lett improvisasjon og det samme ballspillet igjen.'
}, {
  title: 'Leting etter opplegg på nettet',
  text: 'Aktiviteter du finner er for lette, for vanskelige eller må tilpasses før de fungerer i gymsalen.'
}];
export const afterItems = ['Ett ferdig system for hele året, 1.–10. trinn', 'Årsplaner per trinn i tråd med LK20', '12 ferdige timer minutt for minutt', 'Timekort som kan skrives ut direkte', 'Leker, stasjoner og redskapsløyper', 'Ballspill med regler, roller og progresjon', 'Friluftsliv, svømming og livredning', 'Kjennetegn på måloppnåelse og vurderingsskjema', '100 ferdige lærerkommentarer'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Søndagskvelden går med til planlegging',
  text: 'Bruker du søndagskvelden på å planlegge ukas kroppsøvingstimer?'
}, {
  icon: TimerIcon,
  title: 'Elevene står for lenge i kø',
  text: 'Skulle du ønske at timene hadde mer aktivitet, høyere tempo og mindre venting?'
}, {
  icon: UsersIcon,
  title: 'Gruppen har blandede nivåer',
  text: 'Trenger du aktiviteter som fungerer for både de usikre og de mest aktive elevene i samme time?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Rektor spør etter vurdering',
  text: 'Trenger du dokumentasjon på underveisvurdering og måloppnåelse – uten å bruke kveldene på det?'
}, {
  icon: LayoutGridIcon,
  title: 'Klokka er 08.15 og ingenting er klart',
  text: 'Står du i gymsalen med elevene på vei inn – uten en plan for timen?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leter etter opplegg på nettet',
  text: 'Bruker du tid på å lete etter aktiviteter som passer trinnet og kompetansemålene?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volum 01',
  level: 'Alle trinn',
  title: 'Årsplan og 12 ferdige timer',
  tagline: 'Vit nøyaktig hva hver time skal inneholde – hele skoleåret.',
  intro: 'Det første volumet gir deg årsplaner for hvert trinn og tolv ferdige timer du kan bruke direkte i gymsalen.',
  items: ['Årsplan for 1.–2. trinn', 'Årsplan for 3.–4. trinn', 'Årsplan for 5.–7. trinn', 'Årsplan for 8.–10. trinn', '12 timer minutt for minutt', 'Timer på 45 og 60 minutter', 'Progresjonstabell', 'Kompetansemål i praksis', 'Ferdighetstester med fasit', 'Vurderingsmatrise', 'Underveisvurdering'],
  outro: 'Ferdige timer som gjør at du går inn i gymsalen med en tydelig plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volum 02',
  level: '1.–4. trinn',
  title: 'Lek, motorikk og bevegelsesglede',
  tagline: 'Mye bevegelse, mye glede – og lite venting.',
  intro: 'Det andre volumet samler leker og stasjoner som bygger grunnleggende bevegelser hos de yngste elevene.',
  items: ['Leker for de yngste', 'Stasjoner', 'Grunnleggende bevegelser', 'Redskapsløyper', 'Bevegelsesglede i hver time'],
  outro: 'Aktiviteter som holder alle elevene i bevegelse hele timen.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volum 03',
  level: '5.–7. trinn',
  title: 'Ballspill, samarbeid og fair play',
  tagline: 'Spill der alle får være med – og lærer å samarbeide.',
  intro: 'Det tredje volumet gir deg ballspill med tydelige regler, roller og progresjon for mellomtrinnet.',
  items: ['Ballspill for mellomtrinnet', 'Enkle og tydelige regler', 'Roller i spillet', 'Progresjon fra enkelt til krevende', 'Samarbeid og fair play'],
  outro: 'Spill som gir mer ballkontakt og bedre samspill i gruppen.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volum 04',
  level: '1.–10. trinn',
  title: 'Friluftsliv, svømming og livredning',
  tagline: 'Trygge uteøkter og svømmeundervisning med struktur.',
  intro: 'Det fjerde volumet gir deg uteøkter, sikkerhetsrutiner og opplegg for svømmeferdighet og livredning.',
  items: ['Uteøkter og friluftsliv', 'Sikkerhet ute og i vann', 'Svømmeferdighet', 'Livredning', 'Opplegg for alle trinn'],
  outro: 'Struktur og trygghet – både ute i naturen og i bassenget.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volum 05',
  level: '8.–10. trinn',
  title: 'Trening, helse og livsmestring',
  tagline: 'Elevene lærer å trene – og å ta vare på seg selv.',
  intro: 'Det femte volumet gir ungdomstrinnet treningsprinsipper og verktøy for å lage sin egen treningsplan.',
  items: ['Treningsprinsipper', 'Kondisjon', 'Styrke', 'Egen treningsplan', 'Egenvurdering', 'Helse og livsmestring'],
  outro: 'Kunnskap elevene kan bruke langt utenfor gymsalen.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volum 06',
  level: '1.–10. trinn',
  title: 'Vurdering og underveisvurdering',
  tagline: 'Dokumentasjonen på plass – uten ekstra kvelder.',
  intro: 'Det sjette volumet gir deg verktøyene for å vurdere, dokumentere og gi tilbakemelding til hver elev.',
  items: ['Kjennetegn på måloppnåelse', 'Vurderingsskjema', 'Prøver A og B', 'Egenvurdering for elevene', '100 ferdige lærerkommentarer'],
  outro: 'Komplett støtte når rektor spør etter dokumentasjon.',
  note: 'GYMKLAR™ er et selvstendig utviklet materiell og ikke et offisielt produkt fra Utdanningsdirektoratet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale volumer for kroppsøving',
  text: 'Et komplett system som hjelper deg å planlegge og gjennomføre kroppsøvingen gjennom hele skoleåret. Hvert volum har 40 sider med ferdige timer, aktiviteter og vurderingsmateriell klart til utskrift.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ferdige timer minutt for minutt',
  text: 'Slipp å starte med et blankt ark. Tydelige timeopplegg med tider, aktiviteter og instrukser gjør at du kommer raskere i gang.'
}, {
  icon: FileTextIcon,
  title: 'Timekort klare til utskrift',
  text: 'Hele timen på ett kort som du skriver ut og tar med i gymsalen – ikke mer leting på mobilen.'
}, {
  icon: ShapesIcon,
  title: 'Stasjoner og redskapsløyper',
  text: 'Tydelige oppsett for stasjoner, rotasjoner og løyper gjør aktivitetene enkle å forklare og sette i gang.'
}, {
  icon: DicesIcon,
  title: 'Leker og ballspill',
  text: 'Leker, spill og utfordringer gir elevene mer aktivitet, mer glede og mindre venting.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurdering og dokumentasjon',
  text: 'Kjennetegn på måloppnåelse, vurderingsskjema og ferdige kommentarer når du skal dokumentere.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan per trinn',
  text: 'Årsplaner for 1.–10. trinn med kompetansemålene fra LK20 omsatt i praksis.'
}];
export { pageSamples } from './pageSamples';
export const pageTypes = ['Ferdige timer', 'Timekort klare til utskrift', 'Stasjoner og leker', 'Tester og fasit'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikaropplegg som fungerer',
  text: 'Trygge timer som vikaren kan gjennomføre – uten at du må planlegge noe ekstra.',
  items: ['12 trygge vikartimer', 'Overleveringsark til vikaren', 'Tydelige instrukser', 'Klart til utskrift'],
  benefit: 'Syk eller på kurs? Timen går likevel.'
}, {
  title: '5-minutters bevegelsespauser',
  text: 'Korte pauser som gir elevene ny energi – i klasserommet eller i gymsalen.',
  items: ['40 ferdige pauser', '5 minutter hver', 'For klasserom og gymsal'],
  benefit: 'Mer bevegelse i hverdagen – uten ekstra planlegging.'
}, {
  title: 'Bildestøtte for gymsalen',
  text: 'Visuelle kort som gjør timen forutsigbar for alle elever.',
  items: ['Bildekort', 'Dagsplan', 'Stasjonskort'],
  benefit: 'Mindre forklaring – mer aktivitet.'
}, {
  title: 'Aktivitetsdag steg for steg',
  text: 'Alt du trenger for å arrangere en aktivitetsdag for trinnet eller hele skolen.',
  items: ['12 stasjoner', 'Rotasjonsskjema', 'Foreldrebrev'],
  benefit: 'En hel aktivitetsdag – ferdig planlagt.'
}, {
  title: 'Kommunikasjon hjem–skole',
  text: 'Ferdige meldingsmaler til foresatte om timer, utstyr og vurdering.',
  items: ['Ferdige meldingsmaler', 'Til foresatte', 'Klare til å kopiere'],
  benefit: 'Meldingen hjem skrevet på et minutt.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Kjøp GYMKLAR™',
  text: 'Gjennomfør kjøpet på et par minutter. Engangsbetaling, ingen abonnement.'
}, {
  title: 'Last ned PDF-ene på e-post',
  text: 'Du får PDF-ene tilsendt på e-post med en gang etter kjøpet.'
}, {
  title: 'Skriv ut timekortet og gå inn i gymsalen',
  text: 'Velg timen, skriv ut timekortet – og gå inn i gymsalen med en tydelig plan.'
}];
export const audienceItems = ['Er kroppsøvingslærer på 1.–10. trinn', 'Er kontaktlærer og har kroppsøving uten fagbakgrunn', 'Vil ha timer i tråd med LK20 og Fagfornyelsen', 'Har grupper med blandede nivåer', 'Vil slippe å planlegge hver time fra bunnen av', 'Vil ha mer aktivitet og mindre kø i timene', 'Trenger dokumentasjon på vurdering og underveisvurdering', 'Vil ha søndagskvelden tilbake'];
export const timeValueTasks = ['Lete etter aktiviteter på nettet', 'Planlegge timene', 'Lage stasjoner og løyper', 'Lage vurderingsskjema', 'Skrive tilbakemeldinger til elevene'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale volumer – 240 sider',
  detail: 'Årsplan og timer, lek og motorikk, ballspill, friluftsliv og svømming, trening og helse, vurdering'
}, {
  label: '5 bonuser uten ekstra kostnad',
  detail: 'Vikaropplegg, bevegelsespauser, bildestøtte, aktivitetsdag og kommunikasjon hjem–skole'
}, {
  label: 'For 1.–10. trinn',
  detail: 'Ferdige timer, timekort, stasjoner, tester og vurderingsskjema'
}];
export const offerIncludes = ['Direkte nedlasting', 'PDF-filer på e-post', 'Klar til utskrift', 'Ingen fysisk vare sendes'];
export const PRICE = '299 NOK';
export const PRICE_BEFORE = '599 NOK';

// No testimonials on this page. Only authentic reviews may ever be added here.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvilket format har materiellet?',
  a: 'GYMKLAR™ leveres digitalt som PDF-filer. Du kan bruke dem på PC, nettbrett eller mobil og skrive ut sidene du trenger.'
}, {
  q: 'Hvordan får jeg materiellet?',
  a: 'Du får PDF-ene på e-post med en gang etter kjøpet. Ingen ventetid og ingen fysisk vare.'
}, {
  q: 'Passer det for 1.–10. trinn?',
  a: 'Ja. Materiellet dekker hele grunnskolen – fra lek og motorikk på 1.–4. trinn og ballspill på 5.–7. trinn til trening og helse på 8.–10. trinn. Årsplanene er laget per trinn.'
}, {
  q: 'Er det i tråd med LK20?',
  a: 'Ja. Årsplanene og timene bygger på kompetansemålene i kroppsøving etter LK20 / Fagfornyelsen, med kompetansemål i praksis og kjennetegn på måloppnåelse. GYMKLAR™ er et selvstendig materiell og ikke et offisielt produkt fra Utdanningsdirektoratet.'
}, {
  q: 'Kan jeg skrive det ut?',
  a: 'Ja. Alt er laget klart til utskrift. Du velger selv hva du skriver ut, og du kan også bruke materiellet digitalt.'
}, {
  q: 'Fungerer det for kontaktlærere uten bakgrunn i kroppsøving?',
  a: 'Ja. Timene er beskrevet minutt for minutt med tydelige instrukser, så du trenger ikke fagbakgrunn i kroppsøving for å gjennomføre dem.'
}, {
  q: 'Kan jeg virkelig være klar til gymsalen på 5 minutter?',
  a: 'GYMKLAR™ er laget for å gjøre forberedelsene raskere. Du velger en ferdig time, leser gjennom timekortet og er klar på omtrent fem minutter. Tiden kan variere etter timen, gruppen og behovet for tilpasning.'
}, {
  q: 'Er de fem bonusene inkludert?',
  a: `Ja. Alle fem bonusene er inkludert uten ekstra kostnad – alt for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nei. Du betaler én gang og får tilgang til det digitale materiellet. Ingen faste trekk.'
}, {
  q: 'Finnes det en garanti?',
  a: 'Ja. GYMKLAR™ har 7 dager pengene-tilbake-garanti. Hvis materiellet ikke svarer til forventningene dine, kan du kontakte oss og be om å få pengene tilbake innen 7 dager etter kjøpet.'
}];
export const footerLinks = ['Kjøpsvilkår', 'Personvern', 'Refusjon', 'Kontakt'];
